/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { AppContext } from 'Context';
import { Pathname } from 'Routes';
import AXIOS from 'axios';
import { axios, baseUrl } from 'Api';
import { saveUploadInfo, saveUploadDetails, saveDummy, removeDummy, clearAllDummys, clearUploadInfo } from 'store/store';
import { createUploadSSESession, createShortLink } from 'Api/apiFunctions';
import QRCodeComponent from 'Utils/QRCodeComponent';
import { uploadLargeFile } from '../../Utils/uploadWrapper';
import { getBucketName, generateS3Url } from '../../Api/awsConfig';
import uploadManager from '../../Utils/uploadManagerSecure';
import ForNoImage from '../../Assets/Placeholder.png';
import CloudUploadModal from '../../Components/UploadDropdown/CloudUploadModal';
import styles from './QuickUserUploadPage.module.css';

const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.avi', '.mkv', '.wmv', '.flv', '.webm', '.m4v', '.3gp', '.mpg', '.mpeg', '.ts', '.mts'];
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.heic', '.heif', '.tiff', '.tif', '.svg'];
const FILE_ACCEPT = `${[...VIDEO_EXTENSIONS, ...IMAGE_EXTENSIONS].join(',')},video/*,image/*`;

const UPLOAD_STATUS = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  PAUSED: 'paused',
  NETWORK_ERROR: 'network_error',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLING: 'cancelling',
  CANCELLED: 'cancelled',
};

function changeSize(size) {
  const newSize = size / 1024 / 1024;
  return newSize.toFixed(2);
}

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
};

function QuickUserUploadPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const filesInputRef = useRef(null);

  const {
    userDetails,
    setAppSnackbar,
    setUploadContainer,
    setUploadContainer2,
    setIamUploading,
    setDraftCategoryState,
    base64ToBinary,
    autoSignIn,
    navigateToInviteFirstTime,
    handleManger,
    getSubscription,
    getAccessChecker,
    setGetAccessChecker,
  } = useContext(AppContext);

  const userId = userDetails?.isSubscription?.user_id || localStorage.getItem('id');
  const quickUserEmail = sessionStorage.getItem('quickUserEmail') || userDetails?.email || 'Their';

  // Redux for upload tracking
  const uploadPercentages = useSelector((state) => state.upload);
  const updateUploadPercentages = (draftId, data) => {
    dispatch(saveUploadInfo({ [draftId]: data }));
  };
  const updateUploadDetails = (draftId, data) => {
    dispatch(saveUploadDetails({ [draftId]: data }));
  };
  const updateDummy = (dummy, status = 0) => {
    if (status === 1) {
      dispatch(saveDummy({ [dummy?.draft_id]: dummy }));
    } else {
      dispatch(removeDummy(dummy));
    }
  };

  // Upload progress state (gift-flow-style UI)
  const [uploads, setUploads] = useState([]);
  const [isUploadingView, setIsUploadingView] = useState(false);
  const hasLeftUploadPageRef = useRef(false);
  const uploadsRef = useRef([]);
  useEffect(() => { uploadsRef.current = uploads; }, [uploads]);

  // Mobile upload QR modal state (declared before subscription so setShowMobileQr is available)
  const [showMobileQr, setShowMobileQr] = useState(false);
  // Album photos modal for grid view
  const [albumModal, setAlbumModal] = useState(null);
  // Album folder grid class helper (same as gift flow)
  const getAlbumGridClass = (count) => {
    if (count <= 4) return `${styles.albumFolderGrid} ${styles.albumFolderGrid2x2}`;
    if (count <= 6) return `${styles.albumFolderGrid} ${styles.albumFolderGrid3x2}`;
    return styles.albumFolderGrid;
  };
  // Track which mobile photo UIDs we've already counted (prevents re-counting on each subscription tick)
  const trackedMobilePhotoUidsRef = useRef(new Set());
  // Persist the photo album ID so subsequent uploads reuse the same album
  const photoAlbumIdRef = useRef(null);
  // Cache mobile video data (thumbnail, s3Url, s3Key) by fileName so it survives manager auto-removal.
  // Same pattern as mobileThumbCacheRef in app.jsx but local and includes s3 data for videos.
  const mobileVideoCacheRef = useRef({});

  // Subscribe to uploadManagerSecure for real-time progress
  useEffect(() => {
    const unsubscribe = uploadManager.subscribe(({ uploads: managerUploads }) => {
      if (managerUploads.length === 0) return;

      setUploads((prev) => {
        let changed = false;

        // Update existing entries
        let updated = prev.map((upload) => {
          // Match by secureUploadId (local uploads) or by isMobileUpload id
          const managerUpload = upload.secureUploadId
            ? managerUploads.find((u) => u.id === upload.secureUploadId)
            : upload.isMobileUpload
              ? managerUploads.find((u) => u.id === upload.mobileUid)
              : null;
          if (!managerUpload) return upload;
          if (upload.status === UPLOAD_STATUS.COMPLETED) return upload;

          let mappedStatus = upload.status;
          if (managerUpload.status === 'pending') mappedStatus = UPLOAD_STATUS.PENDING;
          else if (managerUpload.status === 'initializing' || managerUpload.status === 'uploading' || managerUpload.status === 'finishing') mappedStatus = UPLOAD_STATUS.UPLOADING;
          else if (managerUpload.status === 'paused') mappedStatus = UPLOAD_STATUS.PAUSED;
          else if (managerUpload.status === 'network_error') mappedStatus = UPLOAD_STATUS.NETWORK_ERROR;
          else if (managerUpload.status === 'failed') mappedStatus = UPLOAD_STATUS.FAILED;
          else if (managerUpload.status === 'cancelled') mappedStatus = UPLOAD_STATUS.CANCELLED;
          else if (managerUpload.status === 's3_complete' || managerUpload.status === 'completed') mappedStatus = UPLOAD_STATUS.COMPLETED;

          if (
            mappedStatus === upload.status
            && managerUpload.progress === upload.progress
            && managerUpload.speed === upload.speed
          ) {
            return upload;
          }

          changed = true;
          // Build thumbnail/s3Url from s3Key when upload completes
          const ext = `.${(upload.fileName || '').split('.').pop().toLowerCase()}`;
          const isImageFile = IMAGE_EXTENSIONS.includes(ext);
          const newS3Url = (!upload.s3Url && managerUpload.s3Key)
            ? `${generateS3Url()}${managerUpload.s3Key}`
            : upload.s3Url;
          const s3Thumb = managerUpload.thumbnail || upload.thumbnail || (isImageFile && newS3Url ? newS3Url : null);
          return {
            ...upload,
            status: mappedStatus,
            progress: Math.max(Math.round(managerUpload.progress || 0), upload.progress || 0),
            bytesUploaded: Math.max(managerUpload.bytesUploaded || 0, upload.bytesUploaded || 0),
            speed: managerUpload.speed || upload.speed,
            timeRemaining: managerUpload.timeRemaining || upload.timeRemaining,
            isPaused: managerUpload.isPaused || false,
            thumbnail: s3Thumb,
            s3Url: newS3Url,
            s3Key: managerUpload.s3Key || upload.s3Key,
          };
        });

        // Pick up new remote (mobile) uploads not yet in state
        const existingMobileVideoUids = new Set(prev.filter((u) => u.isMobileUpload && !u.isPhotoAlbum).map((u) => u.mobileUid));
        const remoteUploads = managerUploads.filter((u) => u.uploadType === 'remote');
        const s3Base = generateS3Url();

        // Populate video cache from existing state entries (preserve data that survived previous ticks)
        prev.forEach((u) => {
          if (u.isMobileUpload && !u.isPhotoAlbum && u.fileName) {
            const cached = mobileVideoCacheRef.current[u.fileName] || {};
            if (u.thumbnail) cached.thumbnail = u.thumbnail;
            if (u.s3Url) cached.s3Url = u.s3Url;
            if (u.s3Key) cached.s3Key = u.s3Key;
            mobileVideoCacheRef.current[u.fileName] = cached;
          }
        });
        // Populate video cache from current manager uploads (may have newer data)
        remoteUploads.forEach((ru) => {
          const ruExt = `.${(ru.fileName || '').split('.').pop().toLowerCase()}`;
          if (!IMAGE_EXTENSIONS.includes(ruExt) && ru.fileName) {
            const cached = mobileVideoCacheRef.current[ru.fileName] || {};
            if (ru.thumbnail) cached.thumbnail = ru.thumbnail;
            if (ru.s3Key) { cached.s3Key = ru.s3Key; cached.s3Url = `${s3Base}${ru.s3Key}`; }
            mobileVideoCacheRef.current[ru.fileName] = cached;
          }
        });

        // Separate remote uploads into videos and photos
        const newVideoEntries = [];
        let hasNewMobilePhotos = false;

        remoteUploads.forEach((u) => {
          const ext = `.${(u.fileName || '').split('.').pop().toLowerCase()}`;
          const isImage = IMAGE_EXTENSIONS.includes(ext);
          if (isImage) {
            // Track in ref (persistent across subscription ticks)
            if (!trackedMobilePhotoUidsRef.current.has(u.id)) {
              trackedMobilePhotoUidsRef.current.add(u.id);
              hasNewMobilePhotos = true;
            }
          } else if (!existingMobileVideoUids.has(u.id)) {
            const s3Url = u.s3Key ? `${s3Base}${u.s3Key}` : null;
            // Best data: manager → cache (survives auto-removal) → null
            const cachedVideo = mobileVideoCacheRef.current[u.fileName] || {};
            newVideoEntries.push({
              id: `mobile-${u.id}`,
              mobileUid: u.id,
              fileName: u.fileName,
              fileSize: u.totalBytes || 0,
              status: u.status === 'completed' ? UPLOAD_STATUS.COMPLETED : UPLOAD_STATUS.UPLOADING,
              progress: u.progress || 0,
              bytesUploaded: u.bytesUploaded || 0,
              speed: u.speed || 0,
              timeRemaining: u.timeRemaining || 0,
              thumbnail: u.thumbnail || cachedVideo.thumbnail || null,
              s3Url: s3Url || cachedVideo.s3Url || null,
              s3Key: u.s3Key || cachedVideo.s3Key || null,
              type: 'video',
              isMobileUpload: true,
            });
          }
        });

        // Compute mobile photo progress from tracked UIDs and merge into desktop album
        const totalMobilePhotos = trackedMobilePhotoUidsRef.current.size;
        if (totalMobilePhotos > 0) {
          // Count how many mobile photos are still actively uploading (in manager and not completed)
          const activeMobilePhotos = Array.from(trackedMobilePhotoUidsRef.current).filter((uid) => {
            const mu = managerUploads.find((m) => m.id === uid);
            return mu && mu.status !== 'completed';
          }).length;
          const completedMobilePhotos = totalMobilePhotos - activeMobilePhotos;
          const allMobileDone = activeMobilePhotos === 0;

          // Build mobile photo entries for thumbnail grid and modal
          // Collect existing album photos so we preserve thumbnails for uploads that
          // have been auto-removed from the manager or re-created without thumbnail data
          const existingAlbumForPhotos = updated.find((u) => u.isPhotoAlbum);
          const existingPhotoMap = new Map(
            (existingAlbumForPhotos?.photos || []).filter((p) => p.isMobile).map((p) => [p.id, p])
          );
          const mobilePhotoEntries = Array.from(trackedMobilePhotoUidsRef.current).map((uid) => {
            const mu = managerUploads.find((m) => m.id === uid);
            const photoId = `mobile-photo-${uid}`;
            const existingPhoto = existingPhotoMap.get(photoId);
            const s3Url = mu?.s3Key ? `${s3Base}${mu.s3Key}` : null;
            // Best thumbnail: manager data → existing album photo → null
            const thumbnailUrl = mu?.thumbnail || s3Url || existingPhoto?.thumbnailUrl || null;
            return {
              id: photoId,
              name: mu?.fileName || existingPhoto?.name || uid,
              thumbnailUrl,
              isMobile: true,
            };
          });

          // Find desktop photo album to merge into
          const desktopAlbum = updated.find((u) => u.isPhotoAlbum && !u.isMobileUpload);
          if (desktopAlbum) {
            const baseDesktopPhotos = desktopAlbum.baseDesktopPhotos ?? desktopAlbum.totalPhotos ?? 0;
            const newTotal = baseDesktopPhotos + totalMobilePhotos;
            const desktopUploaded = desktopAlbum.uploadedPhotos || 0;
            const desktopDone = desktopUploaded >= baseDesktopPhotos;
            const newStatus = (allMobileDone && desktopDone) ? UPLOAD_STATUS.COMPLETED : UPLOAD_STATUS.UPLOADING;

            // Preserve existing photos, update null thumbnails, append new
            const mobilePhotoMap = new Map(mobilePhotoEntries.map((p) => [p.id, p]));
            const existingMobileIds = new Set((desktopAlbum.photos || []).filter((p) => p.isMobile).map((p) => p.id));
            const newMobilePhotos = mobilePhotoEntries.filter((p) => !existingMobileIds.has(p.id));
            let photosChanged = newMobilePhotos.length > 0;
            const existingFixed = (desktopAlbum.photos || []).map((p) => {
              if (p.isMobile && !p.thumbnailUrl && mobilePhotoMap.get(p.id)?.thumbnailUrl) {
                photosChanged = true;
                return { ...p, thumbnailUrl: mobilePhotoMap.get(p.id).thumbnailUrl };
              }
              return p;
            });
            const mergedPhotos = [...existingFixed, ...newMobilePhotos];

            if (photosChanged
              || desktopAlbum.mobilePhotoCount !== totalMobilePhotos
              || desktopAlbum.mobilePhotosCompleted !== completedMobilePhotos
              || desktopAlbum.status !== newStatus) {
              updated = updated.map((u) => (u.id === desktopAlbum.id ? {
                ...u,
                baseDesktopPhotos,
                totalPhotos: newTotal,
                uploadedPhotos: (u.uploadedPhotos || 0) + (completedMobilePhotos - (u.mobilePhotosCompleted || 0)),
                mobilePhotoCount: totalMobilePhotos,
                mobilePhotosCompleted: completedMobilePhotos,
                fileName: `Photo Album (${newTotal} photos)`,
                status: newStatus,
                progress: newStatus === UPLOAD_STATUS.COMPLETED ? 100 : u.progress,
                photos: mergedPhotos,
              } : u));
              changed = true;
            }
          } else {
            // No desktop photo album exists — create a standalone entry for mobile photos
            const mobileAlbumExists = updated.find((u) => u.isPhotoAlbum && u.isMobileUpload);
            if (mobileAlbumExists) {
              // Update existing mobile-only album entry — preserve existing photos, update null thumbnails, add new
              const mobileStatus = allMobileDone ? UPLOAD_STATUS.COMPLETED : UPLOAD_STATUS.UPLOADING;
              const mobilePhotoMap2 = new Map(mobilePhotoEntries.map((p) => [p.id, p]));
              const existingIds = new Set((mobileAlbumExists.photos || []).map((p) => p.id));
              const newPhotos = mobilePhotoEntries.filter((p) => !existingIds.has(p.id));
              let photosChanged2 = newPhotos.length > 0;
              const existingFixed2 = (mobileAlbumExists.photos || []).map((p) => {
                if (p.isMobile && !p.thumbnailUrl && mobilePhotoMap2.get(p.id)?.thumbnailUrl) {
                  photosChanged2 = true;
                  return { ...p, thumbnailUrl: mobilePhotoMap2.get(p.id).thumbnailUrl };
                }
                return p;
              });
              const updatedPhotos = [...existingFixed2, ...newPhotos];
              if (photosChanged2
                || mobileAlbumExists.totalPhotos !== totalMobilePhotos
                || mobileAlbumExists.mobilePhotosCompleted !== completedMobilePhotos
                || mobileAlbumExists.status !== mobileStatus) {
                updated = updated.map((u) => (u.id === mobileAlbumExists.id ? {
                  ...u,
                  totalPhotos: totalMobilePhotos,
                  uploadedPhotos: completedMobilePhotos,
                  mobilePhotoCount: totalMobilePhotos,
                  mobilePhotosCompleted: completedMobilePhotos,
                  fileName: `Photo Album (${totalMobilePhotos} photos)`,
                  status: mobileStatus,
                  progress: mobileStatus === UPLOAD_STATUS.COMPLETED ? 100 : u.progress,
                  photos: updatedPhotos,
                } : u));
                changed = true;
              }
            } else {
              // Create new mobile photo album entry
              updated = [{
                id: `mobile-photo-album-${Date.now()}`,
                fileName: `Photo Album (${totalMobilePhotos} photos)`,
                fileSize: 0,
                status: allMobileDone ? UPLOAD_STATUS.COMPLETED : UPLOAD_STATUS.UPLOADING,
                progress: allMobileDone ? 100 : 0,
                bytesUploaded: 0,
                speed: 0,
                timeRemaining: 0,
                thumbnail: null,
                type: 'photo',
                isPhotoAlbum: true,
                isMobileUpload: true,
                totalPhotos: totalMobilePhotos,
                uploadedPhotos: completedMobilePhotos,
                mobilePhotoCount: totalMobilePhotos,
                mobilePhotosCompleted: completedMobilePhotos,
                photos: mobilePhotoEntries,
              }, ...updated];
              changed = true;
            }
          }
        }

        // Fix null thumbnails/s3Urls in existing mobile video entries from cache
        // (same pattern as photo thumbnail fix with existingFixed / mobilePhotoMap)
        updated = updated.map((u) => {
          if (!u.isMobileUpload || u.isPhotoAlbum) return u;
          const cached = mobileVideoCacheRef.current[u.fileName];
          if (!cached) return u;
          const needThumb = !u.thumbnail && cached.thumbnail;
          const needS3 = !u.s3Url && cached.s3Url;
          const needKey = !u.s3Key && cached.s3Key;
          if (needThumb || needS3 || needKey) {
            changed = true;
            return {
              ...u,
              thumbnail: u.thumbnail || cached.thumbnail || null,
              s3Url: u.s3Url || cached.s3Url || null,
              s3Key: u.s3Key || cached.s3Key || null,
            };
          }
          return u;
        });

        if (newVideoEntries.length > 0 || hasNewMobilePhotos) {
          changed = true;
          // Close QR modal when files arrive from mobile
          setShowMobileQr(false);
          // Switch to uploading view
          setIsUploadingView(true);
          return [...newVideoEntries, ...updated];
        }

        return changed ? updated : prev;
      });
    });

    return unsubscribe;
  }, []);

  // Upload control handlers
  const handlePauseUpload = useCallback((uploadId) => {
    const upload = uploadsRef.current.find((u) => u.id === uploadId);
    if (upload?.secureUploadId) {
      uploadManager.pauseUpload(upload.secureUploadId);
    }
    setUploads((prev) => prev.map((u) => (u.id === uploadId && u.status === UPLOAD_STATUS.UPLOADING
      ? { ...u, status: UPLOAD_STATUS.PAUSED, isPaused: true }
      : u)));
  }, []);

  const handleResumeUpload = useCallback((uploadId) => {
    const upload = uploadsRef.current.find((u) => u.id === uploadId);
    if (!upload) return;
    if (upload.status !== UPLOAD_STATUS.PAUSED && upload.status !== UPLOAD_STATUS.NETWORK_ERROR) return;
    if (upload.secureUploadId) {
      uploadManager.resumeUpload(upload.secureUploadId);
    }
    setUploads((prev) => prev.map((u) => (u.id === uploadId
      ? { ...u, status: UPLOAD_STATUS.UPLOADING, isPaused: false }
      : u)));
  }, []);

  const handleCancelUpload = useCallback((uploadId) => {
    const upload = uploadsRef.current.find((u) => u.id === uploadId);
    if (!upload) return;
    if (upload.secureUploadId) {
      uploadManager.cancelUpload(upload.secureUploadId);
    }
    setUploads((prev) => prev.map((u) => (u.id === uploadId
      ? { ...u, status: UPLOAD_STATUS.CANCELLED }
      : u)));
    setTimeout(() => {
      setUploads((prev) => prev.filter((u) => u.id !== uploadId));
    }, 1000);
  }, []);

  // Content filter tabs (All / Videos / Photos)
  const [contentFilter, setContentFilter] = useState('all');

  // Cloud upload state
  const [cloudModalOpen, setCloudModalOpen] = useState(false);
  const [selectedCloudService, setSelectedCloudService] = useState(null);

  // Mobile upload QR modal state (showMobileQr declared above, before subscription)
  const [mobileSessionId, setMobileSessionId] = useState(null);
  const [mobileQrLoading, setMobileQrLoading] = useState(false);
  const [mobileQrError, setMobileQrError] = useState(null);
  const [mobileShortUrl, setMobileShortUrl] = useState('');

  const handleOpenMobileUpload = async () => {
    setShowMobileQr(true);
    setMobileQrLoading(true);
    setMobileQrError(null);
    setMobileShortUrl('');

    // Retry up to 3 times with short delay
    let id = null;
    for (let attempt = 1; attempt <= 3; attempt += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await createUploadSSESession();
        id = response?.data?.session_id;
        if (id) break;
      } catch (err) {
        console.warn(`[QuickUpload] Session creation attempt ${attempt} failed:`, err);
        if (attempt < 3) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 1000));
        }
      }
    }

    if (!id) {
      setMobileQrError('Failed to create session. Please try again.');
      setMobileQrLoading(false);
      return;
    }

    setMobileSessionId(id);
    localStorage.setItem('mobileUploadSessionId', id);
    window.dispatchEvent(new CustomEvent('mobileUploadSessionCreated', { detail: { sessionId: id } }));

    // Shorten the QR URL (same as gift flow)
    const token = localStorage.getItem('token') || '';
    const uploaderToken = localStorage.getItem('uploader_token') || '';
    const path = `mobile-upload?session=${id}${token ? `&token=${token}` : ''}${uploaderToken ? `&access=${uploaderToken}` : ''}`;
    try {
      const shortRes = await createShortLink({ token, path });
      if (shortRes?.data?.link) {
        setMobileShortUrl(shortRes.data.link);
      }
    } catch (shortErr) {
      console.warn('[QuickUpload] Short link failed, using full URL:', shortErr);
    }

    setMobileQrLoading(false);
  };

  const handleCloseMobileQr = () => {
    setShowMobileQr(false);
    setMobileSessionId(null);
    setMobileQrError(null);
    setMobileShortUrl('');
  };

  const mobileQrUrl = mobileShortUrl
    || (mobileSessionId
      ? `${window.location.origin}/mobile-upload?session=${mobileSessionId}${localStorage.getItem('token') ? `&token=${localStorage.getItem('token')}` : ''}${localStorage.getItem('uploader_token') ? `&access=${localStorage.getItem('uploader_token')}` : ''}`
      : '');

  // Prevent navigating back to this page once left (skip if returning from OAuth)
  useEffect(() => {
    const alreadyVisited = sessionStorage.getItem('quickUploadVisited');
    const isOAuthReturn = sessionStorage.getItem('autoOpenCloudModal');
    if (alreadyVisited && !isOAuthReturn) {
      sessionStorage.removeItem('quickUploadVisited');
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  // Re-open cloud modal after OAuth redirect
  useEffect(() => {
    const autoOpenService = sessionStorage.getItem('autoOpenCloudModal');
    if (autoOpenService) {
      sessionStorage.removeItem('autoOpenCloudModal');
      setSelectedCloudService(autoOpenService);
      setCloudModalOpen(true);
    }
  }, []);

  const leaveUploadPage = (path) => {
    sessionStorage.setItem('quickUploadVisited', 'true');
    navigate(path || '/dashboard', { replace: true });
  };

  const getToken = () => localStorage.getItem('token');

  const [goingBack, setGoingBack] = useState(false);

  const fetchAccessData = async () => {
    try {
      const formData = new FormData();
      formData.append('token', getToken());
      const response = await fetch(`${baseUrl}/getUserAccess`, {
        method: 'POST',
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        setGetAccessChecker(data);
        return data;
      }
    } catch (error) {
      console.error('Error:', error.message);
    }
    return null;
  };

  const goBackToCreateUser = async () => {
    setGoingBack(true);
    try {
      // Fetch access data to get user_token for the switch-back API
      let accessData = getAccessChecker;
      if (!accessData?.data?.user_token) {
        accessData = await fetchAccessData();
      }

      const formData = new FormData();
      formData.append('token', accessData?.data?.user_token || getToken());
      const response = await AXIOS.post(`${baseUrl}/getManagerUserLogs`, formData);

      // Restore the manager FIRST — handleManger returns false when no usable
      // manager token came back (stale access-log row), and then we leave the
      // session untouched instead of clearing the markers and getting logged out.
      if (handleManger(response.data) === false) {
        setGoingBack(false);
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: 'Could not switch back to your account. Please try again.',
        });
        return;
      }
      localStorage.setItem('firstname', '');
      localStorage.setItem('lastname', '');
      // Clear the frontend impersonation markers — banner reads these as a
      // gate on top of the /getUserAccess response.
      localStorage.removeItem('isAccessingUser');
      localStorage.removeItem('accessedUserId');

      dispatch(clearAllDummys());
      setGetAccessChecker(null);
      await autoSignIn();
      await getSubscription();
      dispatch(clearUploadInfo());
      sessionStorage.removeItem('quickUploadVisited');
      sessionStorage.removeItem('quickUserEmail');
      // Let React settle all context/state updates before navigating
      await new Promise((resolve) => { setTimeout(resolve, 300); });
      navigate('/create-user', { replace: true });
    } catch (error) {
      console.error('Error switching back to manager:', error);
      setGoingBack(false);
    }
  };

  // Convert ForNoImage to base64
  const getForNoImageBase64 = async () => {
    const response = await fetch(ForNoImage);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  // Backend fallback for thumbnail generation (sends first 5MB of video)
  const callBackendThumbnailAPI = async (file) => {
    try {
      const videoFilePiece = new FormData();
      videoFilePiece.append('video_file', file.slice(0, 5 * 1024 * 1024));

      const response = await fetch('https://conf.eq7.in/generate-thumbnail/', {
        method: 'POST',
        body: videoFilePiece,
      });

      if (!response.ok) throw new Error(`Server error ${response.status}`);
      const data = await response.json();

      if (data?.thumbnail) {
        const thumbnailDataUrl = `data:image/png;base64,${data.thumbnail}`;
        // Convert data URL to blob
        const res = await fetch(thumbnailDataUrl);
        const blob = await res.blob();
        return [blob];
      }
      return [];
    } catch (err) {
      console.error('[QuickUpload] Backend thumbnail generation failed:', err);
      return [];
    }
  };

  // Generate thumbnails from a video file using canvas (same as VideoUploadModal)
  // Falls back to backend API if client-side generation fails
  const generateThumbnailsFromVideo = (file) => new Promise((resolve) => {
    const video = document.createElement('video');
    const videoUrl = URL.createObjectURL(file);
    video.src = videoUrl;
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;

    const fallbackToBackend = async () => {
      URL.revokeObjectURL(videoUrl);
      video.remove();
      const backendBlobs = await callBackendThumbnailAPI(file);
      resolve({ thumbnailBlobs: backendBlobs, videoHeight: 0, videoWidth: 0 });
    };

    video.onerror = () => {
      fallbackToBackend();
    };

    video.onloadeddata = () => {
      const { duration } = video;
      const vHeight = video.videoHeight;
      const vWidth = video.videoWidth;
      const thumbnailCount = 6;
      const interval = duration / (thumbnailCount + 1);
      const tempBlobs = [];

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      let currentIndex = 1;

      const captureNextFrame = () => {
        if (currentIndex > thumbnailCount) {
          URL.revokeObjectURL(videoUrl);
          video.remove();
          if (tempBlobs.length === 0) {
            // Client-side captured no frames, fall back to backend
            callBackendThumbnailAPI(file).then((backendBlobs) => {
              resolve({ thumbnailBlobs: backendBlobs, videoHeight: vHeight, videoWidth: vWidth });
            });
            return;
          }
          resolve({ thumbnailBlobs: tempBlobs, videoHeight: vHeight, videoWidth: vWidth });
          return;
        }
        video.currentTime = interval * currentIndex;

        video.onseeked = () => {
          canvas.width = vWidth;
          canvas.height = vHeight;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(
            (blob) => {
              if (blob) tempBlobs.push(blob);
              currentIndex += 1;
              captureNextFrame();
            },
            'image/jpeg',
            0.9
          );
        };
      };

      captureNextFrame();
    };
  });

  // Convert a blob to base64
  const blobToBase64 = (blob) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });

  // Add video metadata to API after S3 upload completes
  const addVideoToAPI = async (draftID, draftState, secureUploadData = null) => {
    const formData = new FormData();
    const draftData = draftState[draftID];
    const isSecureUpload = secureUploadData && secureUploadData.s3Key;

    if (draftData) {
      formData.append('draft_id', draftID || '');
      formData.append('title', draftData.title || '');
      formData.append('category_id', draftData.category || '');
      formData.append('file_name', draftData.fileName || '');

      if (isSecureUpload) {
        formData.append('file_key', secureUploadData.s3Key);
        formData.append('document_id', secureUploadData.documentId || '');
        formData.append('bucket_folder_path', secureUploadData.s3Key);

        if (draftData.thumbnail) {
          formData.append('thumbnail', base64ToBinary(draftData.thumbnail));
        }
        if (secureUploadData.thumbnails && Array.isArray(secureUploadData.thumbnails)) {
          secureUploadData.thumbnails.forEach((thumbnail, index) => {
            if (thumbnail && index !== draftData.selectedThumbnailIndex) {
              const binary = typeof thumbnail === 'string' ? base64ToBinary(thumbnail) : thumbnail;
              formData.append('thumbnails[]', binary);
            }
          });
        }
      } else {
        if (draftData.thumbnail) {
          formData.append('thumbnail', base64ToBinary(draftData.thumbnail));
        }
        if (draftData.thumbnails && Array.isArray(draftData.thumbnails)) {
          draftData.thumbnails.forEach((binary, index) => {
            if (binary && index !== draftData.selectedThumbnailIndex) {
              formData.append('thumbnails[]', binary);
            }
          });
        }
        formData.append('uploadKey', draftData.uploadKey || '');
        formData.append('bucket_folder_path', `${draftData.s3Url}${draftData.uploadBucket}/${draftData.uploadKey}`);
        formData.append('s3_image_bucket_url', draftData.s3Url || '');
      }

      formData.append('description', draftData.description || '');
      formData.append('video_size', changeSize(draftData.videoSize) || '');
      formData.append('group_ids', (draftData.selectedGroups || []).join(','));
      formData.append('visibility', draftData.visibility || '');
      formData.append('subcategory_id', draftData.selectedSubCategory || '');
      formData.append('playlist_ids', draftData.selectedPlaylist || '');
      formData.append('videoHeight', draftData.videoHeight || '');
      formData.append('videoWidth', draftData.videoWidth || '');
      formData.append('videoOreintation', draftData.orientation || '');
      formData.append('s3_status', draftData.uploadStatus || '');
    }

    const response = await axios.post(`${baseUrl}/V6/addVideo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  };

  // Upload a single video file through the app's upload manager
  const startVideoUpload = async (videoFile) => {
    const fileExtension = videoFile.name.split('.').pop().toLowerCase();
    const number = Math.floor(Math.random() * 10000) + 1;
    const fileName = `${Date.now()}-${userId}-video-${number}.${fileExtension}`;
    const bucketName = getBucketName(userId);
    const s3Url = generateS3Url();
    const draftID = uuid();
    const videoSize = videoFile.size;

    // Generate real thumbnails from video frames
    const { thumbnailBlobs, videoHeight, videoWidth } = await generateThumbnailsFromVideo(videoFile);

    let thumbnailBase64;
    let thumbnailsBinary;

    if (thumbnailBlobs.length > 0) {
      thumbnailBase64 = await blobToBase64(thumbnailBlobs[0]);
      thumbnailsBinary = await Promise.all(
        thumbnailBlobs.map((blob) => blobToBase64(blob).then((b64) => base64ToBinary(b64)))
      );
    } else {
      thumbnailBase64 = await getForNoImageBase64();
      thumbnailsBinary = [base64ToBinary(thumbnailBase64)];
    }

    // Add to local upload tracking for gift-flow-style UI
    setUploads((prev) => [...prev, {
      id: draftID,
      secureUploadId: draftID,
      fileName: videoFile.name,
      fileSize: videoSize,
      status: UPLOAD_STATUS.PENDING,
      progress: 0,
      bytesUploaded: 0,
      speed: 0,
      timeRemaining: 0,
      thumbnail: thumbnailBase64,
      type: 'video',
    }]);

    const preUploadCount = userDetails?.totalContentUploaded || 0;

    uploadLargeFile({
      userId,
      bucket: bucketName,
      key: fileName,
      file: videoFile,
      draftId: draftID,
      thumbnails: thumbnailsBinary,
      suppressToast: true,
      uploadPercentages,
      updateUploadPercentages,
      setUploadContainer,
      setUploadContainer2,
      setIamUploading,
      dummyData: {
        title: videoFile.name.replace(/\.[^/.]+$/, ''),
        fileName: videoFile.name,
        thumbnail: thumbnailBase64,
        thumbnails: thumbnailsBinary,
        ForNoImage,
        category: 'All Videos',
        description: '',
        videoSize,
        selectedGroups: [],
        changeSize,
      },
      updateDummy,
      setDraftCategoryState,
      onSuccess: async (draftId, secureUploadData = null) => {
        const isSecureUpload = secureUploadData && secureUploadData.s3Key;

        const updatedDraftData = {
          title: videoFile.name.replace(/\.[^/.]+$/, ''),
          category: '',
          fileName: videoFile.name,
          thumbnail: thumbnailBase64,
          thumbnails: thumbnailsBinary,
          selectedThumbnailIndex: 0,
          description: '',
          videoSize,
          selectedGroups: [],
          visibility: '2',
          selectedSubCategory: '',
          selectedPlaylist: '',
          videoHeight,
          videoWidth,
          orientation: videoHeight < videoWidth ? 1 : 2,
          uploadStatus: 'completed',
          uploadKey: isSecureUpload ? secureUploadData.s3Key : fileName,
          uploadBucket: isSecureUpload ? '' : bucketName,
          s3Url: isSecureUpload ? '' : s3Url,
        };

        setDraftCategoryState((prevState) => ({
          ...prevState,
          [draftId]: updatedDraftData,
        }));

        const tempDraftState = { [draftId]: updatedDraftData };

        try {
          await addVideoToAPI(draftId, tempDraftState, secureUploadData);
          await autoSignIn();
          // Only navigate to ecosystem after user has left the upload page via Continue
          if (hasLeftUploadPageRef.current) {
            navigateToInviteFirstTime(preUploadCount);
          }
        } catch (error) {
          console.error('[QuickUpload] Failed to add video to API:', error);
        }
      },
      onError: (error, draftId) => {
        console.error('[QuickUpload] Upload failed for draft:', draftId, error);
      },
      updateUploadDetails,
    });
  };

  // Upload photos as an album through the app's upload manager
  const startPhotoAlbumUpload = async (imageFiles) => {
    // Reuse existing album or find/create "All Photos" album
    let albumId = photoAlbumIdRef.current;
    if (!albumId) {
      // First, check if "All Photos" album already exists
      try {
        const albumListResponse = await axios.post(`${baseUrl}/getAlbumList`, {});
        const existingAlbums = albumListResponse.data?.data || [];
        const allPhotosAlbum = existingAlbums.find((a) => a.title === 'All Photos');
        if (allPhotosAlbum) {
          albumId = allPhotosAlbum.album_id || allPhotosAlbum.id;
          // console.log('[QuickUpload] Found existing "All Photos" album:', albumId);
        }
      } catch (err) {
        console.warn('[QuickUpload] Failed to fetch album list, will create new album:', err);
      }

      // If no existing album found, create a new one
      if (!albumId) {
        const response = await axios.post(`${baseUrl}/addEditAlbum`, {
          title: 'All Photos',
          description: '',
          visibility: '2',
          group_id: '',
          token: getToken(),
        });
        albumId = response.data?.album_id;
        if (!albumId) {
          console.error('[QuickUpload] Failed to create album');
          return;
        }
        // console.log('[QuickUpload] Created new "All Photos" album:', albumId);
      }
      photoAlbumIdRef.current = albumId;
    }

    const totalPhotos = imageFiles.length;

    // Get thumbnail from first photo for the progress card
    const firstPhotoBase64 = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(imageFiles[0]);
    });

    // Build new photo entries
    const totalSize = imageFiles.reduce((sum, f) => sum + f.size, 0);
    const newAlbumPhotos = imageFiles.map((f) => ({
      id: `photo-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      name: f.name,
      thumbnailUrl: URL.createObjectURL(f),
    }));

    // Check if a photo album upload entry already exists (from prior desktop upload or mobile)
    const existingAlbum = uploadsRef.current.find((u) => u.isPhotoAlbum);
    if (existingAlbum) {
      // Merge into existing album entry
      setUploads((prev) => prev.map((u) => {
        if (!u.isPhotoAlbum) return u;
        const mergedPhotos = [...(u.photos || []).filter((p) => !p.isMobile), ...newAlbumPhotos, ...(u.photos || []).filter((p) => p.isMobile)];
        // If entry was mobile-only, desktop base is 0; otherwise use stored baseDesktopPhotos
        const prevDesktopPhotos = u.baseDesktopPhotos ?? (u.isMobileUpload ? 0 : (u.totalPhotos ?? 0));
        const newBaseDesktop = prevDesktopPhotos + totalPhotos;
        const mobileCount = u.mobilePhotoCount || 0;
        const newTotalPhotos = newBaseDesktop + mobileCount;
        return {
          ...u,
          id: albumId,
          status: UPLOAD_STATUS.UPLOADING,
          fileSize: (u.fileSize || 0) + totalSize,
          totalPhotos: newTotalPhotos,
          baseDesktopPhotos: newBaseDesktop,
          uploadedPhotos: u.uploadedPhotos || 0,
          fileName: `Photo Album (${newTotalPhotos} photos)`,
          thumbnail: u.thumbnail || firstPhotoBase64,
          photos: mergedPhotos,
          isMobileUpload: false,
        };
      }));
    } else {
      // Create new album entry
      setUploads((prev) => [...prev, {
        id: albumId,
        fileName: `Photo Album (${totalPhotos} photos)`,
        fileSize: totalSize,
        status: UPLOAD_STATUS.UPLOADING,
        progress: 0,
        bytesUploaded: 0,
        speed: 0,
        timeRemaining: 0,
        thumbnail: firstPhotoBase64,
        type: 'photo',
        isPhotoAlbum: true,
        totalPhotos,
        uploadedPhotos: 0,
        photos: newAlbumPhotos,
      }]);
    }

    // Register with upload manager
    uploadManager.createPhotoUpload({
      albumId,
      albumTitle: 'Photo Album',
      photoCount: totalPhotos,
      updateUploadPercentages,
    });

    updateUploadPercentages(albumId, `0/${totalPhotos}`);

    // Create blob URLs for the image files
    const photoUrls = imageFiles.map((file) => URL.createObjectURL(file));

    // Get icon from first photo
    const iconBase64 = firstPhotoBase64;

    // Track cumulative uploaded count (existing + this batch)
    const existingUploaded = existingAlbum ? (existingAlbum.uploadedPhotos || 0) : 0;
    let successfulUploadCount = 0;

    // Prepare all batches first, then upload sequentially via reduce
    const preparedBatches = await Promise.all(
      photoUrls.map(async (url, i) => {
        try {
          const formData = new FormData();
          formData.append('album_id', albumId);
          formData.append('visibility', '2');
          formData.append('group_id', '');
          if (iconBase64) {
            formData.append('icon', iconBase64);
          }
          formData.append('token', getToken());

          const photoResponse = await fetch(url);
          const blob = await photoResponse.blob();
          formData.append('photo_file[]', blob, imageFiles[i].name);

          return { formData, index: i };
        } catch (error) {
          console.error(`[QuickUpload] Error preparing photo ${i + 1}:`, error);
          return null;
        }
      })
    );

    // Upload sequentially using reduce
    await preparedBatches.reduce(async (previousPromise, batchData) => {
      await previousPromise;
      if (batchData) {
        try {
          await axios.post(`${baseUrl}/addPhotoContent`, batchData.formData);
          successfulUploadCount += 1;
          const cumulativeUploaded = existingUploaded + successfulUploadCount;
          updateUploadPercentages(albumId, `${successfulUploadCount}/${totalPhotos}`);
          uploadManager.updatePhotoUploadProgress(albumId, successfulUploadCount, totalPhotos);
          // Update local progress for photo album (cumulative across batches)
          setUploads((prev) => prev.map((u) => (u.id === albumId
            ? { ...u, progress: Math.round((cumulativeUploaded / (u.baseDesktopPhotos || u.totalPhotos || 1)) * 100), uploadedPhotos: cumulativeUploaded, bytesUploaded: Math.round((cumulativeUploaded / (u.totalPhotos || 1)) * (u.fileSize || 0)) }
            : u)));
        } catch (error) {
          console.error(`[QuickUpload] Error uploading photo ${batchData.index + 1}:`, error);
        }
      }
    }, Promise.resolve());

    updateUploadPercentages(albumId, null);
    uploadManager.completePhotoUpload(albumId);

    // Mark this batch done — only set COMPLETED if all desktop photos are uploaded and no mobile uploads pending
    const finalUploaded = existingUploaded + successfulUploadCount;
    setUploads((prev) => prev.map((u) => {
      if (u.id !== albumId) return u;
      const desktopDone = finalUploaded >= (u.baseDesktopPhotos || u.totalPhotos || 0);
      const mobileDone = !u.mobilePhotoCount || (u.mobilePhotosCompleted >= u.mobilePhotoCount);
      const allDone = desktopDone && mobileDone;
      return { ...u, status: allDone ? UPLOAD_STATUS.COMPLETED : u.status, progress: allDone ? 100 : u.progress, uploadedPhotos: finalUploaded };
    }));

    // Clean up blob URLs
    photoUrls.forEach((url) => URL.revokeObjectURL(url));
  };

  // Handle file selection - starts upload and shows progress UI
  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Capture the input element before any await — React 16 pools synthetic events,
    // so event.target becomes null after the first suspension point.
    const inputElement = event.target;
    // Reset immediately so the same files can be re-selected
    inputElement.value = ''; // eslint-disable-line no-param-reassign

    const videoFiles = [];
    const imageFiles = [];

    files.forEach((file) => {
      const ext = `.${file.name.split('.').pop().toLowerCase()}`;
      if (VIDEO_EXTENSIONS.includes(ext)) videoFiles.push(file);
      else if (IMAGE_EXTENSIONS.includes(ext)) imageFiles.push(file);
    });

    if (videoFiles.length === 0 && imageFiles.length === 0) {
      setAppSnackbar({ isVisible: true, message: 'Please select valid video or image files', type: 'error' });
      return;
    }

    // Show upload progress view instead of navigating away
    setIsUploadingView(true);

    // Start video uploads
    videoFiles.forEach((videoFile) => {
      startVideoUpload(videoFile);
    });

    // Start photo album upload
    if (imageFiles.length > 0) {
      try {
        await startPhotoAlbumUpload(imageFiles);
      } catch (error) {
        console.error('[QuickUpload] Photo album upload failed:', error);
        setAppSnackbar({ isVisible: true, message: 'Failed to upload photos', type: 'error' });
      }
    }
  };

  // Continue button - navigate to accessed user, uploads continue in background
  const handleContinue = () => {
    hasLeftUploadPageRef.current = true;
    leaveUploadPage(Pathname.yourVideos);
  };

  const handleChooseLocalFiles = () => {
    sessionStorage.setItem('quickUploadVisited', 'true');
    filesInputRef.current?.click();
  };

  const handleCloudUpload = (service) => {
    // console.log('[QuickUpload] handleCloudUpload called, service:', service, 'isUploadingView:', isUploadingView);
    // Only mark visited when not already in uploading view (prevents navigation guard from kicking in)
    if (!isUploadingView) {
      sessionStorage.setItem('quickUploadVisited', 'true');
    }

    // Set OAuth return markers so CloudUpload page redirects back here after auth
    sessionStorage.setItem('uploadDropdownCloudConnect', service);
    sessionStorage.setItem('oauthReturnUrl', '/quick-user-upload');

    setSelectedCloudService(service);
    setCloudModalOpen(true);
  };

  const handleCloudModalClose = () => {
    setCloudModalOpen(false);
    setSelectedCloudService(null);
  };

  // Download a cloud file (Dropbox / Google Drive) and return a local File object
  const downloadCloudFile = async (cloudFile, source) => {
    // console.log('[QuickUpload] downloadCloudFile called, file:', cloudFile.name, 'source:', source);
    const cloudServices = localStorage.getItem('cloudConnectedServices');
    const parsed = cloudServices ? JSON.parse(cloudServices) : {};
    const cloudAccessToken = parsed[source]?.accessToken;

    if (!cloudAccessToken) {
      throw new Error(`${source === 'dropbox' ? 'Dropbox' : 'Google Drive'} not connected`);
    }

    let tempUrl = null;
    const headers = {};

    if (source === 'dropbox') {
      const dropboxHeaders = {
        Authorization: `Bearer ${cloudAccessToken}`,
        'Content-Type': 'application/json',
      };
      if (cloudFile.namespaceId) {
        dropboxHeaders['Dropbox-API-Path-Root'] = JSON.stringify({
          '.tag': 'namespace_id',
          namespace_id: cloudFile.namespaceId,
        });
      }
      // console.log('[QuickUpload] Dropbox get_temporary_link id:', cloudFile.id);
      const response = await AXIOS.post(
        'https://api.dropboxapi.com/2/files/get_temporary_link',
        { path: cloudFile.id },
        { headers: dropboxHeaders },
      );
      tempUrl = response.data.link;
    } else if (source === 'googledrive') {
      tempUrl = `https://www.googleapis.com/drive/v3/files/${cloudFile.id}?alt=media`;
      headers.Authorization = `Bearer ${cloudAccessToken}`;
    }

    const downloadResponse = await fetch(tempUrl, { headers });
    if (!downloadResponse.ok) {
      throw new Error(`Failed to download ${cloudFile.name}`);
    }

    const blob = await downloadResponse.blob();
    let mimeType = cloudFile.mimeType || blob.type || '';
    if (!mimeType || mimeType === 'application/octet-stream') {
      const ext = (cloudFile.name || '').split('.').pop().toLowerCase();
      const mimeMap = {
        mp4: 'video/mp4',
        mov: 'video/quicktime',
        avi: 'video/x-msvideo',
        mkv: 'video/x-matroska',
        wmv: 'video/x-ms-wmv',
        flv: 'video/x-flv',
        webm: 'video/webm',
        m4v: 'video/x-m4v',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        webp: 'image/webp',
        heic: 'image/heic',
      };
      mimeType = mimeMap[ext] || 'application/octet-stream';
    }

    return new File([blob], cloudFile.name, { type: mimeType });
  };

  // Process cloud files: download then upload (same as gift flow)
  const processCloudFiles = async (files, source) => {
    // console.log('[QuickUpload] processCloudFiles called, files:', files.length, 'source:', source);
    const videoFiles = [];
    const imageFiles = [];

    files.forEach((file) => {
      const isVideo = file.fileType === 'video' || file.mimeType?.startsWith('video/');
      if (isVideo) videoFiles.push(file);
      else imageFiles.push(file);
    });

    // Show upload view
    setIsUploadingView(true);

    // Add placeholder entries immediately so user sees progress while downloading
    const placeholderIds = {};
    videoFiles.forEach((cloudFile) => {
      const placeholderId = `cloud-${uuid()}`;
      placeholderIds[cloudFile.id || cloudFile.name] = placeholderId;
      setUploads((prev) => [...prev, {
        id: placeholderId,
        fileName: cloudFile.name,
        fileSize: cloudFile.size || 0,
        status: UPLOAD_STATUS.PENDING,
        progress: 0,
        bytesUploaded: 0,
        speed: 0,
        timeRemaining: 0,
        thumbnail: null,
        type: 'video',
        isCloudDownloading: true,
      }]);
    });

    if (imageFiles.length > 0) {
      const photoPlaceholderId = `cloud-photos-${uuid()}`;
      placeholderIds.photoAlbumKey = photoPlaceholderId;
      const totalPhotoSize = imageFiles.reduce((sum, f) => sum + (f.size || 0), 0);
      setUploads((prev) => [...prev, {
        id: photoPlaceholderId,
        fileName: `Photo Album (${imageFiles.length} photos)`,
        fileSize: totalPhotoSize,
        status: UPLOAD_STATUS.PENDING,
        progress: 0,
        bytesUploaded: 0,
        speed: 0,
        timeRemaining: 0,
        thumbnail: null,
        type: 'photo',
        isPhotoAlbum: true,
        isCloudDownloading: true,
        totalPhotos: imageFiles.length,
        uploadedPhotos: 0,
      }]);
    }

    // Download and upload videos sequentially
    await videoFiles.reduce(async (prev, cloudFile) => {
      await prev;
      const placeholderId = placeholderIds[cloudFile.id || cloudFile.name];
      try {
        const localFile = await downloadCloudFile(cloudFile, source);
        // Remove the downloading placeholder — startVideoUpload will add the real entry
        setUploads((prev) => prev.filter((u) => u.id !== placeholderId));
        startVideoUpload(localFile);
      } catch (error) {
        console.error('[QuickUpload] Cloud video download failed:', cloudFile.name, error);
        // Mark placeholder as failed
        setUploads((prev) => prev.map((u) => (u.id === placeholderId
          ? { ...u, status: UPLOAD_STATUS.FAILED, isCloudDownloading: false }
          : u)));
        setAppSnackbar({
          isVisible: true,
          message: `Failed to download ${cloudFile.name}`,
          type: 'error',
        });
      }
    }, Promise.resolve());

    // Download and upload photos as album
    if (imageFiles.length > 0) {
      const photoPlaceholderId = placeholderIds.photoAlbumKey;
      try {
        const localImages = await imageFiles.reduce(async (prev, cloudFile) => {
          const acc = await prev;
          const localFile = await downloadCloudFile(cloudFile, source);
          acc.push(localFile);
          return acc;
        }, Promise.resolve([]));

        if (localImages.length > 0) {
          // Remove the downloading placeholder — startPhotoAlbumUpload will add the real entry
          setUploads((prev) => prev.filter((u) => u.id !== photoPlaceholderId));
          await startPhotoAlbumUpload(localImages);
        }
      } catch (error) {
        console.error('[QuickUpload] Cloud photo download failed:', error);
        // Mark placeholder as failed
        setUploads((prev) => prev.map((u) => (u.id === photoPlaceholderId
          ? { ...u, status: UPLOAD_STATUS.FAILED, isCloudDownloading: false }
          : u)));
        setAppSnackbar({
          isVisible: true,
          message: 'Failed to download photos',
          type: 'error',
        });
      }
    }
  };

  const handleCloudFilesSelected = (files, service) => {
    // console.log('[QuickUpload] handleCloudFilesSelected called, files:', files.length, 'service:', service);
    // console.log('[QuickUpload] files data:', JSON.stringify(files.map((f) => ({ name: f.name, id: f.id, path: f.path, path_display: f.path_display, fileType: f.fileType, mimeType: f.mimeType, size: f.size, namespaceId: f.namespaceId }))));
    if (files.length === 0) return;
    handleCloudModalClose();
    processCloudFiles(files, service);
  };

  /* ── Upload cards — /gift-content's content library, verbatim ───────────────
     The three helpers below mirror gift-content/index.jsx's
     renderUploadCardStatus / renderVideoUploadCard / album cards, so an upload
     here renders exactly like one there: a glass card with a 160px thumbnail
     carrying the accent shimmer while in flight, and an info block holding
     name + progress + pause/resume/cancel, the thin bar and the size line
     (swapped for the title + type icon once the upload completes). */

  const renderTypeIcon = (type) => (
    <span className={styles.contentTypeIcon}>
      {type === 'video' ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="23 7 16 12 23 17 23 7" />
          <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
      )}
    </span>
  );

  const renderCardTitleRow = (title, type) => (
    <div className={styles.contentCardTitleRow}>
      <h4 className={styles.contentCardTitle}>{title}</h4>
      {renderTypeIcon(type)}
    </div>
  );

  // Status block for a card whose upload hasn't completed — name + progress +
  // pause/resume/cancel on one line, the thin bar, then the size / source line.
  const renderUploadCardStatus = (upload, displayName) => {
    if (!upload) return null;
    const showControls = !upload.isMobileUpload && !upload.isPhotoAlbum
      && (upload.status === UPLOAD_STATUS.UPLOADING
        || upload.status === UPLOAD_STATUS.PAUSED
        || upload.status === UPLOAD_STATUS.NETWORK_ERROR);
    // Controls aren't shown in every state (e.g. "Waiting..."), so reserve their
    // space with a hidden placeholder — otherwise the status text shifts when
    // they appear.
    const reserveControlSpace = !showControls && !upload.isMobileUpload && !upload.isPhotoAlbum
      && upload.status !== UPLOAD_STATUS.FAILED;
    const name = displayName || upload.fileName || '';
    const isIndeterminate = upload.isMobileUpload || (upload.isPhotoAlbum && upload.mobilePhotoCount > 0);
    return (
      <>
        <div className={styles.uploadProgressHeader}>
          <div className={styles.filteredCardTitleRow}>
            <span className={styles.uploadFileName}>
              {name.length > 24 ? `${name.substring(0, 24)}...` : name}
            </span>
          </div>
          <div className={styles.uploadStatusActions}>
            <span className={styles.uploadStatus}>
              {upload.status === UPLOAD_STATUS.UPLOADING && upload.isMobileUpload && (
                <span className={styles.mobileUploadStatus}>
                  Uploading from Mobile...
                  <span className={styles.mobileUploadSpinner} />
                </span>
              )}
              {upload.status === UPLOAD_STATUS.UPLOADING && !upload.isMobileUpload && upload.isPhotoAlbum
                && `${upload.uploadedPhotos || 0}/${upload.totalPhotos || 0} photos`}
              {upload.status === UPLOAD_STATUS.UPLOADING && !upload.isMobileUpload && !upload.isPhotoAlbum && `${upload.progress || 0}%`}
              {upload.status === UPLOAD_STATUS.PAUSED && 'Paused'}
              {upload.status === UPLOAD_STATUS.NETWORK_ERROR && 'Network Error - Will Resume'}
              {upload.status === UPLOAD_STATUS.PENDING && 'Waiting...'}
              {upload.status === UPLOAD_STATUS.CANCELLING && 'Cancelling...'}
              {upload.status === UPLOAD_STATUS.CANCELLED && 'Cancelled'}
              {upload.status === UPLOAD_STATUS.FAILED && (
                <button
                  type="button"
                  onClick={() => handleCancelUpload(upload.id)}
                  style={{ cursor: 'pointer', background: 'none', border: 'none', color: 'inherit', font: 'inherit', padding: 0 }}
                  title="Remove"
                >
                  &#10005;
                </button>
              )}
            </span>
            {showControls && (
              <div className={styles.uploadControls}>
                {upload.status === UPLOAD_STATUS.UPLOADING && (
                  <button
                    type="button"
                    className={styles.uploadControlButton}
                    onClick={() => handlePauseUpload(upload.id)}
                    title="Pause upload"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                    </svg>
                  </button>
                )}
                {(upload.status === UPLOAD_STATUS.PAUSED || upload.status === UPLOAD_STATUS.NETWORK_ERROR) && (
                  <button
                    type="button"
                    className={styles.uploadControlButton}
                    onClick={() => handleResumeUpload(upload.id)}
                    title={upload.status === UPLOAD_STATUS.NETWORK_ERROR ? 'Resume when online' : 'Resume upload'}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </button>
                )}
                <button
                  type="button"
                  className={styles.uploadControlButtonCancel}
                  onClick={() => handleCancelUpload(upload.id)}
                  title="Cancel upload"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </button>
              </div>
            )}
            {reserveControlSpace && (
              <div className={styles.uploadControls} style={{ visibility: 'hidden' }} aria-hidden="true">
                <span className={styles.uploadControlButton} />
                <span className={styles.uploadControlButtonCancel} />
              </div>
            )}
          </div>
        </div>
        <div className={styles.uploadProgressBarContainer}>
          <div
            className={`${styles.uploadProgressBar} ${upload.status === UPLOAD_STATUS.FAILED ? styles.uploadProgressFailed : ''} ${upload.status === UPLOAD_STATUS.PAUSED ? styles.uploadProgressPaused : ''} ${isIndeterminate && upload.status === UPLOAD_STATUS.UPLOADING ? styles.uploadProgressIndeterminate : ''}`}
            style={{ width: isIndeterminate ? '100%' : `${upload.progress || 0}%` }}
          />
        </div>
        <div className={styles.uploadProgressDetails}>
          {upload.status === UPLOAD_STATUS.FAILED ? (
            <span className={styles.uploadFailedText}>Failed</span>
          ) : upload.isMobileUpload ? (
            <span>Uploading from mobile</span>
          ) : upload.isPhotoAlbum ? (
            <span>
              {upload.mobilePhotoCount > 0
                ? 'Uploading from mobile'
                : `${upload.uploadedPhotos || 0}/${upload.totalPhotos || 0} photos`}
            </span>
          ) : (
            <span>{formatFileSize(upload.fileSize)}</span>
          )}
        </div>
      </>
    );
  };

  // One media card: thumbnail (album grid / poster / placeholder) + shimmer
  // while in flight, then the status block or the finished title row.
  const renderUploadCard = (upload) => {
    const inProgress = upload.status === UPLOAD_STATUS.UPLOADING
      || upload.status === UPLOAD_STATUS.PENDING
      || upload.status === UPLOAD_STATUS.PAUSED
      || upload.status === UPLOAD_STATUS.NETWORK_ERROR
      || upload.status === UPLOAD_STATUS.CANCELLING;
    const isCompleted = upload.status === UPLOAD_STATUS.COMPLETED;
    const isAlbumGrid = upload.isPhotoAlbum && upload.photos && upload.photos.length > 0;
    return (
      <div key={upload.id} className={`${styles.contentCard}${inProgress ? ` ${styles.uploadingCard}` : ''}`}>
        <div className={styles.contentCardThumbnail}>
          {isAlbumGrid ? (
            <div className={getAlbumGridClass(upload.photos.length)}>
              {upload.photos.slice(0, upload.photos.length > 9 ? 8 : 9).map((photo, idx) => (
                <div key={photo.id || idx}>
                  {photo.thumbnailUrl ? (
                    <img src={photo.thumbnailUrl} alt={photo.name || `Photo ${idx + 1}`} />
                  ) : null}
                </div>
              ))}
              {upload.photos.length > 9 && (
                <div className={styles.albumFolderGridCount}>{`+${upload.photos.length - 8}`}</div>
              )}
            </div>
          ) : upload.thumbnail ? (
            <img src={upload.thumbnail} alt={upload.fileName} />
          ) : upload.isMobileUpload && upload.s3Url && upload.type === 'video' && isCompleted ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            (<video src={upload.s3Url} muted playsInline preload="metadata" />)
          ) : (
            <div className={styles.uploadingCardPlaceholder}>
              {upload.type === 'video' ? (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="#555"><path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" /></svg>
              ) : (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
              )}
            </div>
          )}
          {inProgress && <div className={styles.uploadingCardShimmer} />}
          {upload.isPhotoAlbum && isCompleted && (
            <button
              type="button"
              className={styles.previewEyeButton}
              title="Preview photos"
              onClick={() => setAlbumModal({ photos: upload.photos || [] })}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="12" cy="12" r="3" stroke="white" strokeWidth="2" />
              </svg>
            </button>
          )}
        </div>
        <div className={styles.contentCardInfo}>
          {isCompleted
            ? renderCardTitleRow(
              upload.fileName.length > 24 ? `${upload.fileName.substring(0, 24)}...` : upload.fileName,
              upload.isPhotoAlbum ? 'photo' : upload.type,
            )
            : renderUploadCardStatus(upload)}
        </div>
      </div>
    );
  };

  // Photos tab: an album is expanded into one card per photo.
  const renderPhotoCard = (albumId, photo) => (
    <div key={`${albumId}-${photo.id}`} className={styles.contentCard}>
      <div className={styles.contentCardThumbnail}>
        {photo.thumbnailUrl ? (
          <img src={photo.thumbnailUrl} alt={photo.name} />
        ) : (
          <div className={styles.uploadingCardPlaceholder}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
          </div>
        )}
      </div>
      <div className={styles.contentCardInfo}>
        {renderCardTitleRow(
          (photo.name || '').length > 24 ? `${photo.name.substring(0, 24)}...` : photo.name,
          'photo',
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.page}>
      <div className={styles.contentContainer}>
        {goingBack && (
          <div className={styles.switchOverlay}>
            <svg className={styles.switchSpinner} viewBox="0 0 50 50">
              <circle
                className={styles.switchSpinnerCircle}
                cx="25"
                cy="25"
                r="20"
                fill="none"
                strokeWidth="4"
              />
            </svg>
          </div>
        )}
        <button
          type="button"
          className={styles.backButton}
          onClick={goBackToCreateUser}
          disabled={goingBack}
        >
          BACK
        </button>
        <h2 className={styles.heading}>{`Add Media To ${quickUserEmail}'s Projector`}</h2>

        {!isUploadingView ? (
          <>
            <div className={styles.flipCardContainer}>
              <div className={styles.flipCardFront}>
                <div className={styles.uploadOptionsGrid}>
                  {/* Box 1: Choose Local Files */}
                  <div
                    aria-hidden="true"
                    className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay1}`}
                    onClick={handleChooseLocalFiles}
                  >
                    <div className={styles.uploadOptionIcon}>
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M14 2V8H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12 18V12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9 15L12 12L15 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <span className={styles.uploadOptionText}>Choose Local Files</span>
                  </div>
                  {/* Box 2: Upload From Mobile */}
                  <div
                    aria-hidden="true"
                    className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay2}`}
                    onClick={handleOpenMobileUpload}
                  >
                    <div className={styles.uploadOptionIcon}>
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="5" y="1" width="14" height="22" rx="3" stroke="white" strokeWidth="1.5" />
                        <line x1="10" y1="3.5" x2="14" y2="3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                        <circle cx="12" cy="19" r="1" fill="white" />
                      </svg>
                    </div>
                    <span className={styles.uploadOptionText}>Upload From Mobile</span>
                  </div>
                  {/* Box 3: Apple Photos */}
                  <div
                    aria-hidden="true"
                    className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay3}`}
                    onClick={() => handleCloudUpload('applephotos')}
                  >
                    <div className={styles.uploadOptionIcon}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.05 20.28c-.98.95-2.05.79-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="#999" />
                      </svg>
                    </div>
                    <span className={styles.uploadOptionText}>Apple Photos</span>
                  </div>
                  {/* Box 4: Google Photos */}
                  <div
                    aria-hidden="true"
                    className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay1}`}
                    onClick={() => handleCloudUpload('googlephotos')}
                  >
                    <div className={styles.uploadOptionIcon}>
                      <svg width="40" height="40" viewBox="0 0 122.88 122.88" xmlns="http://www.w3.org/2000/svg">
                        <path d="M30.72,27.97c16.95,0,30.72,13.77,30.72,30.72v2.83H2.83c-1.52,0-2.75-1.23-2.83-2.83 C0,41.73,13.77,27.97,30.72,27.97L30.72,27.97z" fill="#F6B704" />
                        <path d="M94.91,30.72c0,16.95-13.77,30.72-30.72,30.72l-2.75,0V2.83C61.44,1.3,62.67,0,64.19,0 C81.22,0,94.91,13.77,94.91,30.72L94.91,30.72z" fill="#E54335" />
                        <path d="M92.16,94.99c-16.95,0-30.72-13.77-30.72-30.72v-2.83h58.61c1.52,0,2.83,1.23,2.83,2.75 C122.88,81.22,109.11,94.99,92.16,94.99L92.16,94.99L92.16,94.99z" fill="#4280EF" />
                        <path d="M27.97,92.16c0-16.95,13.77-30.72,30.72-30.72h2.75v58.61c0,1.52-1.23,2.75-2.83,2.83 C41.66,122.88,27.97,109.11,27.97,92.16L27.97,92.16L27.97,92.16z" fill="#34A353" />
                      </svg>
                    </div>
                    <span className={styles.uploadOptionText}>Google Photos</span>
                  </div>
                  {/* Box 5: Dropbox */}
                  <div
                    aria-hidden="true"
                    className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay2}`}
                    onClick={() => handleCloudUpload('dropbox')}
                  >
                    <div className={styles.uploadOptionIcon}>
                      <svg width="40" height="40" viewBox="0 0 43 40" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12.5 0L0 8.1l8.6 6.9 12.5-7.8L12.5 0zM0 21.9l12.5 8.1 8.6-7.1-12.5-7.8L0 21.9zM21.1 22.9l8.6 7.1 12.5-8.1-8.6-6.8-12.5 7.8zM42.2 8.1L29.7 0l-8.6 7.2 12.5 7.8 8.6-6.9zM21.2 24.6l-8.6 7.1-4-2.6v2.9l12.6 7.6 12.6-7.6v-2.9l-4 2.6-8.6-7.1z" fill="#0061ff" />
                      </svg>
                    </div>
                    <span className={styles.uploadOptionText}>Dropbox</span>
                  </div>
                  {/* Box 6: Google Drive */}
                  <div
                    aria-hidden="true"
                    className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay3}`}
                    onClick={() => handleCloudUpload('googledrive')}
                  >
                    <div className={styles.uploadOptionIcon}>
                      <svg width="32" height="32" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5l5.4 9.35z" fill="#0066DA" />
                        <path d="M43.65 25L29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3L1.2 52.35c-.8 1.4-1.2 2.95-1.2 4.5h27.5L43.65 25z" fill="#00AC47" />
                        <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75L86.1 57c.8-1.4 1.2-2.95 1.2-4.5H59.85L73.55 76.8z" fill="#EA4335" />
                        <path d="M43.65 25L57.4 1.2c-1.35-.8-2.9-1.2-4.5-1.2H34.35c-1.6 0-3.15.45-4.5 1.2L43.65 25z" fill="#00832D" />
                        <path d="M59.85 53H27.5l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.55c1.6 0 3.15-.45 4.5-1.2L59.85 53z" fill="#2684FC" />
                        <path d="M73.4 26.5l-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.2 28h27.5c0-1.55-.4-3.1-1.2-4.5l-12.75-22z" fill="#FFBA00" />
                      </svg>
                    </div>
                    <span className={styles.uploadOptionText}>Google Drive</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Skip - No Media To Upload box */}
            <div
              aria-hidden="true"
              className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay5}`}
              onClick={() => leaveUploadPage()}
              style={{ marginTop: '20px', width: '100%', maxWidth: '580px', height: 'auto', padding: '20px 16px' }}
            >
              <span className={styles.uploadOptionText}>No Media To Upload</span>
              <span style={{ color: '#ccc', fontSize: '0.75rem', fontFamily: "'Montserrat', sans-serif", fontWeight: 500, textAlign: 'center', lineHeight: 1.4, marginTop: '8px' }}>
                It&apos;s completely ok! You can add as much media as you would like later!
              </span>
            </div>
          </>
        ) : (
          /* Upload Progress View — /gift-content's content library UI */
          (
<div className={styles.uploadProgressContainer}>
            {/* Toolbar row: segmented pill tabs left, quick-add rail right */}
            <div className={styles.contentTypeTabsRow}>
              <div className={styles.contentTypeTabs}>
                {[{ label: 'All', value: 'all' }, { label: 'Videos', value: 'videos' }, { label: 'Photos', value: 'photos' }].map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    className={`${styles.contentTypeTab} ${contentFilter === tab.value ? styles.contentTypeTabActive : ''}`}
                    onClick={() => setContentFilter(tab.value)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Add More Media */}
              <div className={styles.tabUploadOptionsWrap}>
                <p>Add More Media</p>
                <div className={styles.tabUploadOptions}>
                  {/* 1. Choose Files */}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="Choose files"
                    title="Choose files"
                    className={styles.tabUploadOption}
                    onClick={() => filesInputRef.current?.click()}
                    onKeyDown={(e) => { if (e.key === 'Enter') filesInputRef.current?.click(); }}
                  >
                    <svg className={styles.tabUploadSvg} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M14 2V8H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M12 18V12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M9 15L12 12L15 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  {/* 2. Upload From Phone */}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="Upload from mobile"
                    title="Upload from mobile"
                    className={styles.tabUploadOption}
                    onClick={handleOpenMobileUpload}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleOpenMobileUpload(); }}
                  >
                    <svg className={styles.tabUploadSvg} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="5" y="1" width="14" height="22" rx="3" stroke="white" strokeWidth="1.5" />
                      <line x1="10" y1="3.5" x2="14" y2="3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                      <circle cx="12" cy="19" r="1" fill="white" />
                    </svg>
                  </div>
                  {/* 3. Apple Photos */}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="Apple Photos"
                    title="Apple Photos"
                    className={styles.tabUploadOption}
                    onClick={() => handleCloudUpload('applephotos')}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCloudUpload('applephotos'); }}
                  >
                    <svg className={styles.tabUploadSvg} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.05 20.28c-.98.95-2.05.79-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="#f4f7fb" />
                    </svg>
                  </div>
                  {/* 4. Google Photos */}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="Google Photos"
                    title="Google Photos"
                    className={styles.tabUploadOption}
                    onClick={() => handleCloudUpload('googlephotos')}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCloudUpload('googlephotos'); }}
                  >
                    <svg className={styles.tabUploadSvg} viewBox="0 0 122.88 122.88" xmlns="http://www.w3.org/2000/svg">
                      <path d="M30.72,27.97c16.95,0,30.72,13.77,30.72,30.72v2.83H2.83c-1.52,0-2.75-1.23-2.83-2.83 C0,41.73,13.77,27.97,30.72,27.97L30.72,27.97z" fill="#F6B704" />
                      <path d="M94.91,30.72c0,16.95-13.77,30.72-30.72,30.72l-2.75,0V2.83C61.44,1.3,62.67,0,64.19,0 C81.22,0,94.91,13.77,94.91,30.72L94.91,30.72z" fill="#E54335" />
                      <path d="M92.16,94.99c-16.95,0-30.72-13.77-30.72-30.72v-2.83h58.61c1.52,0,2.83,1.23,2.83,2.75 C122.88,81.22,109.11,94.99,92.16,94.99L92.16,94.99L92.16,94.99z" fill="#4280EF" />
                      <path d="M27.97,92.16c0-16.95,13.77-30.72,30.72-30.72h2.75v58.61c0,1.52-1.23,2.75-2.83,2.83 C41.66,122.88,27.97,109.11,27.97,92.16L27.97,92.16L27.97,92.16z" fill="#34A353" />
                    </svg>
                  </div>
                  {/* 5. Dropbox */}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="Dropbox"
                    title="Dropbox"
                    className={styles.tabUploadOption}
                    onClick={() => handleCloudUpload('dropbox')}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCloudUpload('dropbox'); }}
                  >
                    <svg className={styles.tabUploadSvg} viewBox="0 0 43 40" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.5 0L0 8.1l8.6 6.9 12.5-7.8L12.5 0zM0 21.9l12.5 8.1 8.6-7.1-12.5-7.8L0 21.9zM21.1 22.9l8.6 7.1 12.5-8.1-8.6-6.8-12.5 7.8zM42.2 8.1L29.7 0l-8.6 7.2 12.5 7.8 8.6-6.9zM21.2 24.6l-8.6 7.1-4-2.6v2.9l12.6 7.6 12.6-7.6v-2.9l-4 2.6-8.6-7.1z" fill="#0061ff" />
                    </svg>
                  </div>
                  {/* 6. Google Drive */}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="Google Drive"
                    title="Google Drive"
                    className={styles.tabUploadOption}
                    onClick={() => handleCloudUpload('googledrive')}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCloudUpload('googledrive'); }}
                  >
                    <svg className={styles.tabUploadSvg} viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5l5.4 9.35z" fill="#0066DA" />
                      <path d="M43.65 25L29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3L1.2 52.35c-.8 1.4-1.2 2.95-1.2 4.5h27.5L43.65 25z" fill="#00AC47" />
                      <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75L86.1 57c.8-1.4 1.2-2.95 1.2-4.5H59.85L73.55 76.8z" fill="#EA4335" />
                      <path d="M43.65 25L57.4 1.2c-1.35-.8-2.9-1.2-4.5-1.2H34.35c-1.6 0-3.15.45-4.5 1.2L43.65 25z" fill="#00832D" />
                      <path d="M59.85 53H27.5l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.55c1.6 0 3.15-.45 4.5-1.2L59.85 53z" fill="#2684FC" />
                      <path d="M73.4 26.5l-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.2 28h27.5c0-1.55-.4-3.1-1.2-4.5l-12.75-22z" fill="#FFBA00" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Content grid */}
            <div className={styles.contentGrid}>
              {(() => {
                const filteredUploads = contentFilter === 'all' ? uploads : uploads.filter((u) => {
                  if (contentFilter === 'videos') return u.type === 'video';
                  if (contentFilter === 'photos') return u.type === 'photo' || u.isPhotoAlbum;
                  return true;
                });

                if (filteredUploads.length === 0) {
                  return (
                    <div className={styles.emptyState}>
                      <div className={styles.emptyStateIcon}>
                        {contentFilter === 'videos' ? '\u{1F3A5}' : contentFilter === 'photos' ? '\u{1F4F7}' : '\u{2B06}\u{FE0F}'}
                      </div>
                      <p>{contentFilter === 'videos' ? 'No videos' : contentFilter === 'photos' ? 'No photos' : 'No uploads yet'}</p>
                    </div>
                  );
                }

                return filteredUploads.flatMap((upload) => {
                  // Photos tab: expand an album into individual photo cards.
                  if (contentFilter === 'photos' && upload.isPhotoAlbum && upload.photos && upload.photos.length > 0) {
                    return upload.photos.map((photo) => renderPhotoCard(upload.id, photo));
                  }
                  return [renderUploadCard(upload)];
                });
              })()}
            </div>

            {/* Bottom strip: count pills + continue */}
            <div className={styles.step5BottomSection}>
              <div className={styles.progressStatusContainer}>
                <span className={styles.uploadStatItem}>
                  {(() => {
                    const pCount = uploads.reduce((count, u) => count + ((u.type === 'photo' || u.isPhotoAlbum) ? (u.totalPhotos || 1) : 0), 0);
                    const vCount = uploads.filter((u) => u.type === 'video').length;
                    const parts = [];
                    if (pCount > 0) parts.push(`${pCount} ${pCount === 1 ? 'photo' : 'photos'}`);
                    if (vCount > 0) parts.push(`${vCount} ${vCount === 1 ? 'video' : 'videos'}`);
                    return parts.length > 0 ? parts.join(', ') : '0 photos, 0 videos';
                  })()}
                </span>

                {uploads.filter((u) => u.status === UPLOAD_STATUS.PENDING).length > 0 && (
                  <span className={styles.uploadStatItem}>
                    {`${uploads.filter((u) => u.status === UPLOAD_STATUS.PENDING).length} in queue`}
                  </span>
                )}

                {uploads.filter((u) => u.status === UPLOAD_STATUS.PAUSED).length > 0 && (
                  <span className={styles.uploadStatItem}>
                    {`${uploads.filter((u) => u.status === UPLOAD_STATUS.PAUSED).length} paused`}
                  </span>
                )}

                {uploads.some((u) => u.status === UPLOAD_STATUS.UPLOADING && !u.isMobileUpload) && (
                  <span className={styles.uploadStatSpeed}>
                    {`${formatFileSize(uploads.filter((u) => u.status === UPLOAD_STATUS.UPLOADING && !u.isMobileUpload).reduce((sum, u) => sum + (u.speed || 0), 0))}/s`}
                  </span>
                )}
              </div>

              <p className={styles.continueHint}>
                You do not need to wait for uploads to complete to continue.
              </p>
              <button
                type="button"
                className={styles.startUploadButton}
                onClick={handleContinue}
              >
                Continue
              </button>
            </div>
</div>
)
        )}
      </div>
      {/* Hidden file input for combined video + photo picker */}
      <input
        type="file"
        ref={filesInputRef}
        style={{ display: 'none' }}
        accept={FILE_ACCEPT}
        multiple
        onChange={handleFileSelect}
      />
      {/* Cloud Upload Modal (Dropbox / Google Drive / Google Photos) */}
      <CloudUploadModal
        open={cloudModalOpen}
        onClose={handleCloudModalClose}
        service={selectedCloudService}
        onFilesSelected={handleCloudFilesSelected}
        onShowMessage={(message) => setAppSnackbar({
          isVisible: true,
          message,
          type: 'default',
        })}
      />
      {/* Mobile Upload QR Popup (same style as gift flow step 3) */}
      {showMobileQr && (
        <div
          className={styles.qrOverlay}
          onClick={handleCloseMobileQr}
          onKeyDown={(e) => { if (e.key === 'Escape') handleCloseMobileQr(); }}
          role="button"
          tabIndex={0}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
          <div className={styles.qrModal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            {/* Header */}
            <div className={styles.qrModalHeader}>
              <div className={styles.qrModalHeaderLeft}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 12 }}>
                  <rect x="5" y="1" width="14" height="22" rx="3" stroke="#5aa5ef" strokeWidth="1.5" />
                  <line x1="10" y1="3.5" x2="14" y2="3.5" stroke="#5aa5ef" strokeWidth="1.5" strokeLinecap="round" />
                  <circle cx="12" cy="19" r="1" fill="#5aa5ef" />
                </svg>
                <h2 className={styles.qrModalTitle}>Scan to Upload</h2>
              </div>
              <button className={styles.qrModalCloseBtn} onClick={handleCloseMobileQr} type="button">
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor" />
                </svg>
              </button>
            </div>
            {/* Content */}
            <div className={styles.qrModalContent}>
              {mobileQrLoading && (
                <div className={styles.qrModalCentered}>
                  <div className={styles.qrSpinner} />
                  <p className={styles.qrModalHint}>Creating session...</p>
                </div>
              )}
              {mobileQrError && (
                <div className={styles.qrModalCentered}>
                  <p className={styles.qrModalError}>{mobileQrError}</p>
                  <button type="button" className={styles.qrRetryBtn} onClick={handleOpenMobileUpload}>
                    Try Again
                  </button>
                </div>
              )}
              {!mobileQrLoading && !mobileQrError && mobileSessionId && (
                <>
                  <div className={styles.qrModalQrWrapper}>
                    <QRCodeComponent url={mobileQrUrl} size={200} />
                  </div>
                  <p className={styles.qrModalHint}>Scan with your phone camera to upload files from your mobile device</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Album photos modal — same as mobile-upload */}
      {albumModal && (
        <div
          className={styles.albumGridOverlay}
          role="button"
          tabIndex={0}
          onClick={() => setAlbumModal(null)}
          onKeyDown={(e) => { if (e.key === 'Escape') setAlbumModal(null); }}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div
            role="dialog"
            aria-modal="true"
            className={styles.albumGridModal}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <button type="button" className={styles.albumGridModalClose} onClick={() => setAlbumModal(null)}>
              &times;
            </button>
            <div className={styles.albumGridModalHeader}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#5aa5ef"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
              {`Photo Album (${albumModal.photos?.length || 0} photos)`}
            </div>
            <div className={styles.albumGridModalBody}>
              {albumModal.photos && albumModal.photos.length > 0 ? (
                <div className={styles.albumGridPhotos}>
                  {albumModal.photos.map((photo) => (
                    <div key={photo.id} className={styles.albumGridPhotoItem}>
                      {photo.thumbnailUrl ? (
                        <img src={photo.thumbnailUrl} alt={photo.name} />
                      ) : (
                        <div className={styles.albumGridPhotoPlaceholder}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
                        </div>
                      )}
                      <span className={styles.albumGridPhotoName}>{photo.name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>No photos</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QuickUserUploadPage;
